'use client'

import { useRef } from 'react'
import { Send, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import type { ChatInputProps } from './types'

/**
 * Shared chat input with send button. Supports a compact mode for
 * the copilot side-panel and a default mode for the full-page view.
 */
export function ChatInput({
  value,
  onChange,
  onSubmit,
  isProcessing,
  isRegistered,
  placeholder,
  compact,
  className,
}: ChatInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <form onSubmit={onSubmit} className={cn('flex gap-2', className)}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || (!isRegistered ? 'Connecting...' : 'Type your message...')}
        disabled={isProcessing || !isRegistered}
        className={cn(
          'flex-1 min-w-0 rounded-lg border border-input bg-background text-sm ring-offset-background',
          'placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          compact ? 'px-3 py-2' : 'px-4 py-3',
        )}
      />
      <Button
        type="submit"
        disabled={isProcessing || !value.trim() || !isRegistered}
        size={compact ? 'default' : 'default'}
        className={compact ? 'px-3' : 'px-6'}
      >
        {isProcessing ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Send className="size-4" />
        )}
      </Button>
    </form>
  )
}
